import { Row, Col, message, Table, Form, Select, Button, Empty, Typography } from "antd";
import { ColumnsType } from "antd/lib/table";
import { AppPageHeader } from "components/AppPageHeader";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useDocumentTitle } from "hooks/usePageTitle";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { getCities, getDarkStores } from "store/admin-slice/adminServicesSlice";
import {
  getDarkStoreReturnChallan,
  getDarkStoreReturns,
  resetDarkStoreReturns,
} from "store/dark-store-return/darkStoreReturnSlice";
import { IDarkStoreReturnItem } from "common/interface/IDarkStoreReturn";
import { openInNewTab } from "common/utils";

const { Text } = Typography;
const MESSAGE_KEY = "ppd__item_return";

type PartialItem = IDarkStoreReturnItem & { key: string; returnId: number; createdOn?: string };

const ItemReturnPage = () => {
  useDocumentTitle("Item Return");
  const dispatch = useAppDispatch();

  const { darkStores, cities } = useAppSelector((state) => state.adminServices);
  const { returns, status, error } = useAppSelector((state) => state.darkStoreReturn);

  const [cityId, setCityId] = useState<string>();
  const [storeId, setStoreId] = useState<string>();
  const [challanLoadingId, setChallanLoadingId] = useState<number>();

  useEffect(() => {
    dispatch(getCities());
    dispatch(getDarkStores());
  }, [dispatch]);

  useEffect(() => {
    if (status === "error") {
      message.error({ content: error, key: MESSAGE_KEY, duration: 3 });
    }
  }, [status, error]);

  const filteredStores = (darkStores ?? []).filter((s) => s.cityId?.toString() === cityId);

  const handleCityChange = (value: string) => {
    setCityId(value);
    setStoreId(undefined);
    dispatch(resetDarkStoreReturns());
  };

  const handleStoreChange = (value: string) => {
    setStoreId(value);
    dispatch(resetDarkStoreReturns());
    dispatch(getDarkStoreReturns({ storeId: Number(value) }));
  };

  const downloadChallan = useCallback(
    (returnId: number) => {
      setChallanLoadingId(returnId);
      dispatch(getDarkStoreReturnChallan(returnId))
        .unwrap()
        .then(({ challanUrl }) => {
          openInNewTab(challanUrl);
        })
        .catch((err) => {
          message.error(err?.message || "Could not generate challan");
        })
        .finally(() => {
          setChallanLoadingId(undefined);
        });
    },
    [dispatch]
  );

  const itemRows: PartialItem[] = (Array.isArray(returns) ? returns : []).flatMap((ret) =>
    (Array.isArray(ret?.items) ? ret.items : []).map((item, i) => ({
      ...item,
      returnId: ret.id,
      createdOn: ret.createdOn,
      key: `${ret.id}-${i}`,
    }))
  );

  const totalQuantity = itemRows.reduce((sum, item) => sum + (item.returnedQty ?? 0), 0);

  const itemColumns: ColumnsType<PartialItem> = [
    { title: "Return ID", dataIndex: "returnId", key: "returnId", width: 100 },
    { title: "SKU Code", dataIndex: "skuCode", key: "skuCode", width: 110 },
    { title: "Product Name", dataIndex: "productName", key: "productName" },
    { title: "Grade", dataIndex: "grade", key: "grade", width: 100 },
    {
      title: "Quantity",
      key: "returnedQty",
      width: 140,
      render: (_: unknown, record: PartialItem) =>
        `${record.returnedQty ?? 0}${record.uom ? ` ${record.uom}` : ""}`,
    },
    {
      title: "Returned On",
      dataIndex: "createdOn",
      key: "createdOn",
      width: 180,
      render: (createdOn: string) => (createdOn ? moment(createdOn).format("DD MMM YYYY, hh:mm A") : "-"),
    },
    {
      title: "Challan",
      key: "challan",
      width: 140,
      render: (_: unknown, record: PartialItem) => {
        const cachedUrl = returns?.find((ret) => ret.id === record.returnId)?.challanUrl;
        return (
          <Button
            size="small"
            loading={challanLoadingId === record.returnId}
            onClick={() => {
              if (cachedUrl) {
                openInNewTab(cachedUrl);
              } else {
                downloadChallan(record.returnId);
              }
            }}
          >
            Download
          </Button>
        );
      },
    },
  ];

  return (
    <>
      <Row>
        <Col>
          <AppPageHeader title="Item Return" />
        </Col>
      </Row>

      <Form layout="vertical" className="mb-8">
        <Row gutter={24}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Form.Item label="City" className="mb-4">
              <Select
                showSearch
                placeholder="Select city"
                className="w-full"
                value={cityId}
                onChange={handleCityChange}
                filterOption={(input, option) =>
                  option?.children?.toString().toLowerCase().includes(input.toLowerCase()) ?? false
                }
              >
                {(cities ?? []).map((city) => (
                  <Select.Option value={city.id?.toString()} key={city.id}>
                    {city.name} || {city.id}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>

          {cityId && (
            <Col xs={24} sm={12} md={8} lg={6}>
              <Form.Item label="Store" className="mb-4">
                <Select
                  showSearch
                  placeholder="Select store"
                  className="w-full"
                  value={storeId}
                  onChange={handleStoreChange}
                  filterOption={(input, option) =>
                    option?.children?.toString().toLowerCase().includes(input.toLowerCase()) ?? false
                  }
                >
                  {filteredStores.map((store) => (
                    <Select.Option value={store.id?.toString()} key={store.id}>
                      {store.name} || {store.id}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          )}
        </Row>
      </Form>

      {storeId && (
        <>
          {returns && (
            <Row style={{ marginBottom: 12 }}>
              <Col>
                <Text strong>
                  {itemRows.length} item{itemRows.length === 1 ? "" : "s"} returned || Total Quantity:{" "}
                  {totalQuantity}
                </Text>
              </Col>
            </Row>
          )}

          <Table
            columns={itemColumns}
            dataSource={itemRows}
            scroll={{ x: "100vh", y: "65vh" }}
            size="small"
            pagination={false}
            loading={status === "loading"}
            bordered
            locale={{
              emptyText: (
                <Empty
                  description={
                    <Text type="secondary" style={{ fontSize: 16 }}>
                      No Items Returned for this Store
                    </Text>
                  }
                />
              ),
            }}
          />
        </>
      )}
    </>
  );
};

export default ItemReturnPage;
